import React from 'react';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	FormLabel,
	Stack,
	TextField,
	FormControl,
	Select,
	MenuItem,
	FormHelperText,
	useTheme,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { Form, Formik } from 'formik';
import { useGetUserDetailsQuery } from '@/features/user/userApi';

import {
	useGetEmailTemplatesQuery,
	useSaveEmailTemplateMutation,
} from '@/features/templates/templatesApi';
import FolioRadioGroup from '@/components/FolioRadioGroup';
import validationSchema from './validationSchema';
import {
	adjustHtmlTablePadding,
	convertToHandlebarsTemplate,
	EMAIL_TEMPLATE_TYPES,
} from '../../../utils/emailHelper';

export const SaveEmailTemplateDialog = ({
	open,
	handleClose,
	htmlValue,
	headContent,
	templateType,
	existingTemplate,
}) => {
	const dispatch = useDispatch();
	const theme = useTheme();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { data: existingTemplates } = useGetEmailTemplatesQuery(
		{ ownerDocId: userDetails?.userId },
		{ skip: !userDetails?.userId },
	);

	const { requirements } = convertToHandlebarsTemplate(htmlValue);

	const [saveEmailTemplate] = useSaveEmailTemplateMutation();

	const initialValues = {
		name: '',
		type: templateType ?? '',
		shareIds: [],
		html: htmlValue,
		requirements,
		existingTemplates,
		replaceExisting: false,
	};

	const soaType = requirements.includes('statementOfAdvice');
	// useEffect(() => console.log('Handlebars template', initialValues.html), [initialValues.html]);

	return (
		<>
			<Formik
				{...{
					initialValues,
					validateOnChange: false,
					validateOnBlur: true,
					validationSchema,
					enableReinitialize: true,
					onSubmit: async (values, { resetForm, setFieldValue, setSubmitting }) => {
						console.log('📋 ~ Formik values', { values, htmlValue });

						try {
							let replaceExisting = values.replaceExisting;
							if (
								values.replaceExisting &&
								(existingTemplate?.master ||
									!existingTemplate?.canUpdate ||
									!existingTemplate?.aiGenerated)
							) {
								replaceExisting = false;
							}
							const newHtml = adjustHtmlTablePadding(htmlValue, headContent);
							const { template: handlebarsTemplate, requirements } =
								convertToHandlebarsTemplate(newHtml);

							const res = await saveEmailTemplate({
								type: values.type,
								html: handlebarsTemplate,
								name: values.name,
								userIds: [],
								brokerGroupIds: [],
								requirements,
								...(replaceExisting &&
									existingTemplate?.id && {
										docId: existingTemplate.id,
										name: existingTemplate.name,
										type: existingTemplate.type,
									}),
							}).unwrap();

							console.log('Here is the res', res);
							if (res) {
								dispatch(
									showToast({
										message: 'Email template saved successfully!',
										autohide: true,
									}),
								);
							}
							handleClose();
						} catch (err) {
							console.log('🙅 ~ Error saving template', err);
							dispatch(
								showToast({
									error: true,
									message: 'Error saving template. Please try again.',
								}),
							);
						}
						setSubmitting(false);
					},
				}}
			>
				{({
					isSubmitting,
					setFieldValue,
					values,
					handleSubmit,
					handleChange,
					touched,
					errors,
					getFieldProps,
					resetForm,
					validateField,
					validateForm,
					setErrors,
					setTouched,
				}) => {
					const typeError =
						touched.type &&
						(Boolean(errors.type) || (soaType && values.type !== 'statementOfAdvice'));

					return (
						<Form>
							<Dialog
								open={open}
								onClose={(event, reason) => {
									if (reason && reason === 'backdropClick' && isSubmitting)
										return;
									handleClose();
								}}
								hideBackdrop={true}
								sx={{
									padding: '1em',
									'& .MuiDialog-paper': {
										width: '60%',
									},
								}}
							>
								<DialogTitle>Save email template</DialogTitle>
								<DialogContent sx={{ paddingBottom: '2em' }}>
									<Stack spacing={2}>
										<FormControl fullWidth>
											<FolioRadioGroup
												name={'replaceExisting'}
												disabled={
													existingTemplate?.master ||
													!existingTemplate?.canUpdate ||
													!existingTemplate?.aiGenerated
												}
												value={values.replaceExisting}
												onChange={(e) => {
													const replaceExisting = ['true', true].includes(
														e.target.value,
													);
													setFieldValue(
														'replaceExisting',
														replaceExisting,
													);
													if (replaceExisting) {
														setFieldValue(
															'name',
															existingTemplate?.name,
														);
													}
												}}
												items={[
													{
														value: false,
														label: 'Create new',
													},
													{
														value: true,
														label: 'Replace existing',
													},
												]}
											/>
										</FormControl>
										<FormControl
											required
											error={touched.name && Boolean(errors.name)}
											fullWidth
											sx={{ width: '100%' }}
										>
											<FormLabel>{'Template name'}</FormLabel>
											<TextField
												fullWidth={true}
												name='name'
												placeholder='Template name'
												{...getFieldProps('name')}
												error={touched.name && Boolean(errors.name)}
												helperText={touched.name && errors.name}
												disabled={isSubmitting || values.replaceExisting}
											/>
										</FormControl>
										<FormControl
											required
											error={touched.type && Boolean(errors.type)}
											fullWidth
											sx={{ width: '100%' }}
										>
											<FormLabel>{'Template type'}</FormLabel>
											<Select
												disabled={isSubmitting || values.replaceExisting}
												{...getFieldProps('type')}
												error={typeError}
											>
												{Object.entries(EMAIL_TEMPLATE_TYPES).map(
													([type, label], index) => (
														<MenuItem
															key={`email-template-${type}-${index}`}
															value={type}
														>
															{label}
														</MenuItem>
													),
												)}
											</Select>
											{typeError && (
												<FormHelperText error>{errors.type}</FormHelperText>
											)}
										</FormControl>
										<FormHelperText>
											{
												'Please note that any changes to tables will not be reflected in saved templates at this point in time.'
											}
										</FormHelperText>
									</Stack>
								</DialogContent>
								<DialogActions
									sx={{ backgroundColor: theme.palette.action.hover, padding: '1.5em' }}
								>
									<Button
										onClick={handleClose}
										disabled={isSubmitting}
										color='primary'
									>
										Cancel
									</Button>
									<Button
										disabled={(values.name ?? '').length === 0 || isSubmitting}
										variant='contained'
										color='primary'
										onClick={handleSubmit}
									>
										Save Template
									</Button>
								</DialogActions>
							</Dialog>
						</Form>
					);
				}}
			</Formik>
		</>
	);
};
